import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for managing table state (filters, sorting, pagination)
 * Centralizes all table-related state management
 */
export const useTableState = (onFiltersChange, filterData, initialFilters = {}) => {
  const [filteredInfo, setFilteredInfo] = useState(initialFilters);
  const [sortedInfo, setSortedInfo] = useState({
    columnKey: 'totalLost',
    order: 'descend'
  });
  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  // Handle table changes (filtering, sorting)
  const handleTableChange = useCallback((tablePagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Notify parent component about filter changes (for faceted search)
    if (onFiltersChange) {
      onFiltersChange(filters, filterData, sorter);
    }
  }, [filterData, onFiltersChange]);

  // Handle removing a single filter
  const handleRemoveFilter = useCallback((columnKey, value) => {
    const newFilters = { ...filteredInfo };
    
    if (newFilters[columnKey]) {
      newFilters[columnKey] = newFilters[columnKey].filter(v => v !== value);
      if (newFilters[columnKey].length === 0) {
        delete newFilters[columnKey];
      }
    }
    
    setFilteredInfo(newFilters);
    
    // Notify parent component for server-side filtering
    if (onFiltersChange) {
      onFiltersChange(newFilters, filterData, sortedInfo);
    }
  }, [filteredInfo, filterData, sortedInfo, onFiltersChange]);

  // Handle clearing all filters
  const handleClearAllFilters = useCallback(() => {
    setFilteredInfo({});
    
    // Notify parent component for server-side filtering
    if (onFiltersChange) {
      onFiltersChange({}, filterData, sortedInfo);
    }
  }, [filterData, sortedInfo, onFiltersChange]);

  return {
    // State
    filteredInfo,
    sortedInfo,
    hoveredRowKey,
    
    // Setters
    setFilteredInfo,
    setSortedInfo,
    setHoveredRowKey,
    
    // Actions
    handleTableChange,
    handleRemoveFilter,
    handleClearAllFilters
  };
};

/**
 * Custom hook for managing toast notifications
 * Provides consistent toast behavior with fallback support
 */
export const useToastNotifications = (externalShowToast) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  // Show toast notification - use external showToast if available, otherwise use internal
  const showToast = useCallback((message, severity = 'success') => {
    if (externalShowToast) {
      externalShowToast(message, severity);
    } else {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    }
  }, [externalShowToast]);

  // Handle toast close
  const handleToastClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  }, []);

  return {
    // State
    toastOpen,
    toastMessage, 
    toastSeverity,
    
    // Actions
    showToast,
    handleToastClose
  };
};

/**
 * Custom hook for managing view mode and UI state
 * Handles view switching, card expansion, etc.
 */
export const useViewState = (initialViewMode = 'table') => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupingStrategy, setGroupingStrategy] = useState('store_cluster');

  // Handle view mode change
  const handleViewModeChange = useCallback((newViewMode) => {
    setViewMode(newViewMode);
  }, []);

  // Handle card expand/collapse
  const handleCardExpand = useCallback((cardKey, expanded) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardKey]: expanded
    }));
  }, []);

  // Handle group expand/collapse (for accordion groups)
  const handleGroupToggle = useCallback((groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  }, []);

  // Handle grouping strategy change
  const handleGroupingStrategyChange = useCallback((strategy) => {
    setGroupingStrategy(strategy);
    // Reset expanded groups when strategy changes
    setExpandedGroups({});
  }, []);

  return {
    // State
    viewMode,
    expandedCards,
    expandedGroups,
    groupingStrategy,
    
    // Actions
    handleViewModeChange,
    handleCardExpand,
    handleGroupToggle,
    handleGroupingStrategyChange,
    setGroupingStrategy
  };
};

/**
 * Custom hook for managing dropdown visibility state
 * Handles dropdown state and click-outside behavior
 */
export const useDropdownVisibility = () => {
  const [showGroupingDropdown, setShowGroupingDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilterColumn, setSelectedFilterColumn] = useState(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside the grouping controls
      if (showGroupingDropdown && !event.target.closest('[data-grouping-control]')) {
        setShowGroupingDropdown(false);
      }
      // Close filter dropdown if clicking outside the filter controls
      if (showFilterDropdown && !event.target.closest('[data-filter-control]')) {
        setShowFilterDropdown(false);
        setSelectedFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGroupingDropdown, showFilterDropdown]);

  // Toggle grouping dropdown
  const toggleGroupingDropdown = useCallback(() => {
    setShowGroupingDropdown(prev => !prev);
  }, []);

  // Toggle filter dropdown
  const toggleFilterDropdown = useCallback(() => {
    setShowFilterDropdown(prev => !prev);
    setSelectedFilterColumn(null);
  }, []);

  // Close all dropdowns
  const closeAllDropdowns = useCallback(() => {
    setShowGroupingDropdown(false);
    setShowFilterDropdown(false);
    setSelectedFilterColumn(null);
  }, []);

  return {
    // State
    showGroupingDropdown,
    showFilterDropdown,
    selectedFilterColumn,
    
    // Actions
    toggleGroupingDropdown,
    toggleFilterDropdown,
    closeAllDropdowns,
    setSelectedFilterColumn
  };
};

/**
 * Custom hook for managing date selections in table
 * Handles date picker state and date submission logic
 */
export const useDateSelections = () => {
  const [tableDateSelections, setTableDateSelections] = useState({});

  // Set date for specific record
  const setDateForRecord = useCallback((recordId, date) => {
    setTableDateSelections(prev => ({
      ...prev,
      [recordId]: date
    }));
  }, []);

  // Clear date for specific record
  const clearDateForRecord = useCallback((recordId) => {
    setTableDateSelections(prev => {
      const updated = { ...prev };
      delete updated[recordId];
      return updated;
    });
  }, []);

  // Get date for specific record
  const getDateForRecord = useCallback((recordId) => {
    return tableDateSelections[recordId];
  }, [tableDateSelections]);

  return {
    // State
    tableDateSelections,
    
    // Actions
    setDateForRecord,
    clearDateForRecord,
    getDateForRecord,
    setTableDateSelections
  };
};


/**
 * Master hook that combines all Technical Alerts state management
 * Provides a single interface for all state needs
 * Note: Dropdown state removed - now handled locally in ControlBar
 */
export const useTechnicalAlertsState = ({
  onFiltersChange,
  filterData,
  externalShowToast,
  initialViewMode = 'table',
  initialFilters = {}
} = {}) => {
  const tableState = useTableState(onFiltersChange, filterData, initialFilters);
  const toastState = useToastNotifications(externalShowToast);
  const viewState = useViewState(initialViewMode);
  const dateState = useDateSelections();

  return {
    // Grouped state
    table: tableState,
    toast: toastState,
    view: viewState,
    date: dateState,
    // dropdown state removed - now local to ControlBar
  };
};

// Column configs factory
export const useColumnConfigs = () => {
  const { t } = useTranslation();
  
  return useMemo(() => ({
    group: { title: t('group') },
    location: { title: t('location') },
    machineId: { title: t('machineId') },
    route: { title: t('route') },
    productName: { title: t('productName') },
    weeksSince: { title: t('weeksSince') },
    alertType: { title: t('alertType') },
    actions: { title: t('status') }
  }), [t]);
};
